/**
 * PRP-WEB-2 W2.7: the underlay's state on the plan page. Pick uploads the image
 * (`POST /plan/underlay`, owner-only) and lays it under the plan as wide as the plan; Set scale
 * takes a line drawn on it and the length the user types; Move picture drags it. Changes are
 * saved with the plan (Save), never on their own.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Plan, Pt } from '@sketch-core';
import { planApi, type PlanUnderlay } from '../../../../lib/planApi';
import type { PromptSpec } from '../menus/menuModel';
import { calibrate, moveBy, placeNew, toSaved, type UnderlayState } from './underlay';
import type { UnderlayMode } from './UnderlayControls';

function loadSize(url: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => reject(new Error('That picture could not be opened.'));
    img.src = url;
  });
}

export function useUnderlay(opts: {
  projectId: string;
  plan: Plan | null;
  saved: PlanUnderlay | null;
  fromServer: { url: string; u: PlanUnderlay } | null;
  prompt: (p: PromptSpec) => void;
}) {
  const { projectId, plan, saved, fromServer, prompt } = opts;
  const [u, setU] = useState<UnderlayState | null>(null);
  const [mode, setMode] = useState<UnderlayMode>('none');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [line, setLine] = useState<[Pt, Pt] | null>(null);
  const drag = useRef<{ w0: Pt; start: UnderlayState } | null>(null);

  // The saved picture, as the server signs it (re-read on every load).
  useEffect(() => {
    let off = false;
    if (!fromServer) { setU(null); return undefined; }
    loadSize(fromServer.url)
      .then(({ w, h }) => { if (!off) setU({ ...fromServer.u, url: fromServer.url, natW: w, natH: h }); })
      .catch(() => { if (!off) setError('The saved picture could not be loaded.'); });
    return () => { off = true; };
  }, [fromServer]);

  const pick = useCallback(async (f: File) => {
    if (!plan) return;
    setBusy(true);
    setError(null);
    try {
      const url = URL.createObjectURL(f);
      const { w, h } = await loadSize(url);
      const { path } = await planApi.uploadUnderlay(projectId, f);
      setU({ path, url, natW: w, natH: h, ...placeNew(plan, w) });
      setMode('calibrate');
    } catch (err) {
      setError((err as Error)?.message || 'The picture could not be uploaded.');
    } finally {
      setBusy(false);
    }
  }, [plan, projectId]);

  const savedNow = u ? toSaved(u) : null;
  const dirty = JSON.stringify(savedNow) !== JSON.stringify(saved ? toSaved({ ...saved, url: '', natW: 0, natH: 0 }) : null);

  /** A press while a picture mode is on: the drag is the picture's. */
  const begin = (wp: Pt) => {
    if (!u || mode === 'none') return false;
    drag.current = { w0: wp, start: u };
    if (mode === 'calibrate') setLine([wp, wp]);
    return true;
  };
  const move = (wp: Pt) => {
    const D = drag.current;
    if (!D) return;
    if (mode === 'move') setU({ ...D.start, ...moveBy(D.start, wp.x - D.w0.x, wp.y - D.w0.y) });
    else setLine([D.w0, wp]);
  };
  const end = () => {
    const D = drag.current;
    drag.current = null;
    if (!D || mode !== 'calibrate' || !line) return;
    const [a, b] = line;
    prompt({
      kind: 'length', title: 'How long is the line you drew?', valueFt: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
      apply: (ft) => {
        const next = calibrate(D.start, a, b, ft);
        if (!next) return 'Draw a longer line first.';
        setU({ ...D.start, ...next });
        setLine(null);
        // B-7562: calibration done ends the picture mode. Left in Move picture, every later
        // click on the plan dragged the picture instead of selecting a room.
        setMode('none');
        return undefined;
      },
    });
  };
  const cancel = () => {
    const D = drag.current;
    drag.current = null;
    if (D) setU(D.start);
    setLine(null);
  };

  return {
    u, mode, setMode: (m: UnderlayMode) => { setMode(m); setLine(null); }, busy, error, line, dirty, saved: savedNow,
    pick, remove: () => { setU(null); setMode('none'); setLine(null); },
    opacity: (v: number) => setU((x) => (x ? { ...x, opacity: v } : x)),
    begin, move, end, cancel, active: () => !!drag.current,
  };
}

export type UnderlayApi = ReturnType<typeof useUnderlay>;
